import React, { useCallback } from 'react';
import { StyleSheet, View } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import {
  BodyText,
  Button,
  IllusStoreOpening,
  Image,
  TextLink,
  theme,
  TitleText,
} from '@sweetgreen/garnish';

import { useLocalizationContext } from '@order/Localization';

import type { AuthRedirectTarget } from '../../navigation/AppNavigation.props';
import { PageWrapper } from '../PageWrapper';

// ────────────────────────────────────────────────────────────────────────────────

export const SignedOutView = (props: SignedOutViewProps) => {
  const { redirectTo = 'home', withoutFooter } = props;

  const { t } = useLocalizationContext();

  const { navigate } = useNavigation();

  // ─── Helpers ─────────────────────────────────────────────────────

  const handlePressSignIn = useCallback(() => {
    navigate('Auth', {
      screen: 'Login',
      params: { redirect: redirectTo },
    });
  }, [navigate, redirectTo]);

  const handlePressJoin = useCallback(() => {
    navigate('Auth', {
      screen: 'Join',
      params: { redirect: redirectTo },
    });
  }, [navigate, redirectTo]);

  // ─────────────────────────────────────────────────────────────────

  return (
    <PageWrapper safeAreaEdges={['top']} withoutFooter={withoutFooter}>
      <View testID="signed-out-view" style={styles.container}>
        <View style={styles.info}>
          <Image
            source={IllusStoreOpening}
            style={styles.image}
            accessibilityIgnoresInvertColors
            contentFit="contain"
          />

          <TitleText size={5}>{t('signed-out-view.welcome')}</TitleText>
        </View>

        <View style={styles.row}>
          <Button
            accessibilityLabel={t('signed-out-view.sign-in')}
            accessibilityHint={t('signed-out-view.sign-in')}
            onPress={handlePressSignIn}
            style={styles.signInButton}
            size="large"
            palette="primary"
          >
            {t('signed-out-view.sign-in')}
          </Button>
        </View>

        <View style={styles.row}>
          <BodyText size={3}>{t('signed-out-view.not-joined')}</BodyText>

          <TextLink
            accessibilityLabel={t('signed-out-view.join-now')}
            accessibilityHint={t('signed-out-view.join-now')}
            onPress={handlePressJoin}
            style={styles.linkWrapper}
          >
            {t('signed-out-view.join-now')}
          </TextLink>
        </View>
      </View>
    </PageWrapper>
  );
};

// ─── Props ───────────────────────────────────────────────────────────────────

type SignedOutViewProps = Readonly<{
  redirectTo?: AuthRedirectTarget;
  withoutFooter?: boolean;
}>;

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    paddingVertical: theme.spacing['6'],
    paddingHorizontal: theme.spacing['4'],
  },
  info: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  signInButton: {
    marginVertical: theme.spacing['4'],
    marginHorizontal: 'auto',
    maxWidth: '100%',
    width: 340,
  },
  row: {
    flexDirection: 'row',
    justifyContent: 'center',
  },
  linkWrapper: {
    marginLeft: theme.spacing['1'],
  },
  image: {
    height: 240,
    width: 240,
  },
});
